import type { ComponentPropsWithRef } from "react";
import { forwardRef, useEffect, useRef } from "react";
import classNames from "classnames";

interface EmbedData {
  html: string;
  url: string;
}

export interface EmbedProps extends ComponentPropsWithRef<"div"> {
  "data-preview-mode"?: boolean;
  embed: EmbedData;
  height?: string | number;
  width?: string | number;
}

export const Embed = forwardRef<HTMLDivElement, EmbedProps>(
  (
    {
      embed,
      className,
      "data-preview-mode": isBuilderPreviewMode,
      height,
      width,
      ...rest
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (!containerRef.current || !embed) return;

      containerRef.current.innerHTML = "";

      const tempContainer = document.createElement("div");
      tempContainer.innerHTML = embed.html;

      const embedElement = tempContainer.firstElementChild;

      if (embedElement) {
        if (isBuilderPreviewMode) {
          embedElement.classList.add("pointer-events-none");
        }
        embedElement.setAttribute("width", width ? `${width}` : "100%");

        if (height !== undefined) {
          embedElement.setAttribute("height", `${height}`);
        }

        containerRef.current.appendChild(embedElement);
      } else {
        containerRef.current.innerHTML = embed.html;
      }
    }, [embed, height, width, isBuilderPreviewMode]);

    return (
      <div
        ref={ref}
        className={classNames(
          "wb-w-full",
          {
            "wb-min-h-64": !embed.html,
          },
          className,
        )}
        {...rest}
      >
        {embed.html && (
          <div
            ref={containerRef}
            className="wb-bg-primary wb-h-full wb-w-full"
          />
        )}
      </div>
    );
  },
);

Embed.displayName = "Embed";
